/**
 * Procedural foliage, built inside the envelope the game's own tree object
 * measures out.
 *
 * ## What the original ships — and what it is
 *
 * AD-019 (`docs/engineering/sprite-geometry-and-palettes.md`) settled the
 * question this module used to hedge. The shipped tree is **a polygon trunk
 * carrying three type-1 SPHERE nodes**: the original's renderer hands a sphere
 * node to a *circle rasteriser* at a screen radius of `radius / depth`. A
 * canopy is therefore a flat filled disc — camera-facing because a circle
 * always is, and scaling with range because a sprite blit would not.
 *
 * The port already draws those discs authentically, in
 * `three/sphere-instances.ts`, and **that path is not touched by anything in
 * this file**. What is here is the ENHANCED TERRAIN alternative: a solid with
 * a shape, standing in the same place, inside the same envelope, in the same
 * palette entries — drawn *instead of* the discs when the setting is on
 * (`terrain-decoration.ts` hides them), and not drawn at all when it is off.
 *
 * ## Measured, and therefore not up for redesign
 *
 * Every one of these is read off `TERR5-83` (and its four identical siblings)
 * in the bake, and pinned in `apps/web/test/terrain-decoration.test.ts`:
 *
 * | | value | where from |
 * |---|---|---|
 * | canopy radius | **4.677 m** | the three sphere nodes' own `radius` |
 * | canopy centre height | **7.692 m** | their `position.z` |
 * | canopy colours | `BARON.PAL` **133** `#516534`, **134** `#556134` | their `colour` byte |
 * | trunk colours | `BARON.PAL` **120** `#594534`, **123** `#614d3c` | the six trunk faces' `colourFront` |
 * | crown span | **3.02 m to 12.37 m** above the trunk foot | centre ± radius |
 * | copse spread | **47 x 20 m** | the object's own extent |
 *
 * A variant here fills that envelope and never exceeds it: no lobe's outer
 * surface passes `CANOPY_RADIUS_M` from the trunk axis, and no crown rises
 * above `CANOPY_CENTRE_M + CANOPY_RADIUS_M`. So an enhanced tree is the same
 * height and the same width as the tree the game places, and G20's arithmetic
 * (a 14 m tree is a couple of pixels from 6 000 ft) is unchanged by it.
 *
 * ## Invented, and it is worth being exact about how much
 *
 * **Everything about the shape below the envelope is invented.** Specifically:
 * that a broadleaf crown is four overlapping lobes rather than one solid; where
 * those lobes sit; that a conifer is three tapering skirts; that the trunk
 * tapers; the facet counts; and which lobe is lit and which is shaded. The
 * original carries none of that information — it carries three spheres and six
 * triangles. There are **no leaves and no branches** modelled at all: at 14 m
 * and this triangle budget a leaf is far below a pixel, so the lobes *are* the
 * foliage.
 *
 * What the invention is disciplined by is the palette. Every colour a variant
 * emits is a real neighbouring `BARON.PAL` entry reached by walking
 * `CANOPY_RUN` — the same run the ground relief and the hour shift walk — so a
 * crown has a lit top and a shaded underside without a single colour that is
 * not in the game.
 *
 * **And the walk is deliberately short.** A lobe's top takes its own step and
 * its underside one below; the steps themselves span only +1 to -1. An earlier
 * version added another step for the lit face, which put the top lobe two
 * entries up the run at `#615934`/`#655d38` — the olive-*brown* end of it. Seen
 * from 740 ft that is what a crown mostly shows, and every tree read as a pale
 * khaki mushroom cap rather than as foliage. The run is shared with the ploughed
 * ground for a reason: walk far enough up it and you arrive at the ploughed
 * ground's colour.
 *
 * ## Why solid geometry and not a billboard, still
 *
 * u6-opus's foliage is alpha-tested cards over a procedural atlas, which is the
 * right answer for a renderer with textures. This one has none: every surface
 * in this port is flat-shaded vertex colour through `BARON.PAL`, and an
 * alpha-cut leaf card would be the first texture in the world. Solid low-poly
 * crowns keep the look, need no atlas, and — the reason that matters most here
 * — need **no alpha at all**, so the pools stay opaque and none of
 * lessons.md's reversed-Z depth traps are in play.
 *
 * It is also why the crowns do not shimmer at range. A dense field of small
 * triangles normally aliases, but every triangle in a crown is one of at most
 * five entries out of one six-entry palette run, and the extremes of that run
 * differ by about 0.06 in linear luminance. Whatever the rasteriser picks per
 * pixel, the aggregate cannot move far enough to sparkle.
 *
 * ## Budget
 *
 * **56 triangles for a broadleaf, 38 for a conifer**, average 47 across the
 * four-variant set, against the previous drum-and-cone pair's 40/24 (average
 * 32) — a 1.47x rise for a shape that reads as a tree instead of a lollipop.
 * Branch limbs were prototyped and cut: they cost 12 triangles a tree and are
 * only ever visible standing directly under one, because a crown starts 3.8 m
 * up and swallows them.
 *
 * The counts are pinned, so a future edit that quietly triples them fails a
 * test rather than a frame budget on somebody's laptop.
 *
 * ## Winding is load-bearing here, and it is the one place in this app that it is
 *
 * `terrain-decoration.ts` gives the trees their own `MeshBasicMaterial` at
 * three's default `FrontSide`, unlike `three/world-instancer.ts`'s `DoubleSide`
 * scenery material. A backwards-wound crown therefore culls its *near* faces
 * and draws the inside of its far side — the hollow-mask illusion, which reads
 * as a flat card that tips toward the camera and swings as it moves (user,
 * 2026-08-23: "the trees are angled with the camera").
 *
 * Every part emitted below is a **closed solid**, wound counter-clockwise seen
 * from outside. That is checkable without knowing anything about where a part
 * sits: a closed outward-wound surface has a **positive signed volume**, and
 * signed volume is origin-independent. `partRanges` exposes the triangle span
 * of each part so the test can assert it per part rather than in aggregate —
 * which the old radial-normal check could not do once lobes moved off the
 * trunk axis.
 */

import * as THREE from "three/webgpu";
import { colourToRgb, type PaletteColour } from "../world/terrain-palette.ts";

/** Sphere-node radius on the shipped tree object, metres. */
export const CANOPY_RADIUS_M = 4.676923076923077;
/** Sphere-node centre height on the shipped tree object, metres. */
export const CANOPY_CENTRE_M = 7.6923076923076925;

/** The two canopy entries the shipped tree's sphere nodes carry. */
export const CANOPY_COLOURS: readonly PaletteColour[] = [0x516534, 0x556134];
/** The two trunk entries its trunk faces carry. */
export const TRUNK_COLOURS: readonly PaletteColour[] = [0x594534, 0x614d3c];

/** Facets round a crown lobe and round a conifer skirt. Six is where a silhouette stops reading as a polygon at 14 m. */
const LOBE_SIDES = 6;
/** Facets round a trunk. */
const TRUNK_SIDES = 5;

type V3 = readonly [number, number, number];

interface Accum {
  readonly positions: number[];
  readonly colors: number[];
  /** Triangle index each part started at — see the module doc on signed volume. */
  readonly partStarts: number[];
}

function pushTriangle(into: Accum, a: V3, b: V3, c: V3, colour: PaletteColour): void {
  const [r, g, bl] = colourToRgb(colour);
  for (const v of [a, b, c]) into.positions.push(v[0], v[1], v[2]);
  for (let i = 0; i < 3; i++) into.colors.push(r, g, bl);
}

/** Marks the start of a new closed solid, so the winding test can check each one alone. */
function beginPart(into: Accum): void {
  into.partStarts.push(into.positions.length / 9);
}

/**
 * The run a canopy colour is nudged along.
 *
 * Not an arithmetic tint: these are consecutive real `BARON.PAL` entries in the
 * ground relief's own green run, which is why a "shade step" here produces a
 * colour the original could have drawn.
 */
export const CANOPY_RUN: readonly PaletteColour[] = [0x2c4520, 0x516534, 0x556134, 0x615934, 0x655d38, 0x6d693c];

/**
 * The highest entry of `CANOPY_RUN` a crown may use at noon.
 *
 * Index 2 is `#556134`, the lighter of the two the game itself paints its
 * canopies with. Above it the run turns to ploughed-earth olive — see the
 * module doc's note on the khaki mushroom caps.
 */
export const CANOPY_RUN_CEILING = 2;

/**
 * A crown colour, `step` entries along `CANOPY_RUN` from `base`, clamped at
 * `CANOPY_RUN_CEILING`.
 *
 * The ceiling is the load-bearing half. The run continues past the greens into
 * the ploughed-earth olives, and the hour shift only ever walks *down* it, so
 * nothing needs the top three entries and a crown that reaches them stops
 * looking like foliage. The clamp is what makes it impossible rather than
 * merely unintended: the light variant's top lobe asked for index 3 and got it
 * until this was here.
 */
function shiftedCanopy(base: PaletteColour, step: number): PaletteColour {
  const at = CANOPY_RUN.indexOf(base);
  if (at < 0) return base;
  return CANOPY_RUN[Math.max(0, Math.min(CANOPY_RUN_CEILING, at + step))]!;
}

/** `sides` points evenly round a circle of radius `r` at height `y`, centred on `(cx, cz)`. */
function ring(sides: number, r: number, y: number, cx: number, cz: number): V3[] {
  const out: V3[] = [];
  for (let i = 0; i < sides; i++) {
    const a = (i / sides) * Math.PI * 2;
    out.push([cx + Math.cos(a) * r, y, cz + Math.sin(a) * r]);
  }
  return out;
}

/**
 * The lateral surface of a cone from `base` up or down to `apexY`.
 *
 * The ring is walked **backwards** for an upward cone and forwards for a
 * downward one: the apex crossing the base plane flips the sense, so the two
 * cases cannot share one order. See the module doc for why an unlit,
 * flat-shaded tree cares at all.
 */
function coneSkin(into: Accum, base: readonly V3[], apex: V3, colour: PaletteColour): void {
  const up = apex[1] > base[0]![1];
  for (let i = 0; i < base.length; i++) {
    const p0 = base[i]!;
    const p1 = base[(i + 1) % base.length]!;
    if (up) pushTriangle(into, p1, p0, apex, colour);
    else pushTriangle(into, p0, p1, apex, colour);
  }
}

/** Caps a ring with a downward-facing fan, so the part above it is a closed solid. */
function capDownwards(into: Accum, base: readonly V3[], colour: PaletteColour): void {
  for (let i = 1; i < base.length - 1; i++) {
    pushTriangle(into, base[0]!, base[i]!, base[i + 1]!, colour);
  }
}

/**
 * One crown lobe: a hexagonal bipyramid, lit above and shaded below.
 *
 * Twelve triangles, and four of them overlapping at different heights is what
 * turns a crown from a smooth blob into something with a top, a shoulder and an
 * underside — which is the whole difference between "a tree" and "a green coin"
 * at the ranges a pilot sees one from.
 */
function lobe(
  into: Accum,
  centre: V3,
  radius: number,
  upM: number,
  downM: number,
  lit: PaletteColour,
  shaded: PaletteColour,
): void {
  beginPart(into);
  const base = ring(LOBE_SIDES, radius, centre[1], centre[0], centre[2]);
  coneSkin(into, base, [centre[0], centre[1] + upM, centre[2]], lit);
  coneSkin(into, base, [centre[0], centre[1] - downM, centre[2]], shaded);
}

/**
 * A conifer skirt: a downward-flaring tier, capped underneath.
 *
 * Ten triangles. Three of them stacked and tapering is a fir; the single tall
 * cone this replaced was a traffic bollard.
 */
function skirt(
  into: Accum,
  baseY: number,
  apexY: number,
  radius: number,
  lit: PaletteColour,
  shaded: PaletteColour,
): void {
  beginPart(into);
  const base = ring(LOBE_SIDES, radius, baseY, 0, 0);
  coneSkin(into, base, [0, apexY, 0], lit);
  capDownwards(into, base, shaded);
}

/**
 * The trunk: a tapering spike from the ground to a point inside the crown.
 *
 * Eight triangles, and it is capped at the foot so it is a closed solid like
 * everything else — the cap is under the drape and never rasterised, which is
 * cheaper than special-casing the winding test.
 */
function trunkSpike(into: Accum, baseRadius: number, apexY: number, colour: PaletteColour): void {
  beginPart(into);
  const base = ring(TRUNK_SIDES, baseRadius, 0, 0, 0);
  coneSkin(into, base, [0, apexY, 0], colour);
  capDownwards(into, base, colour);
}

function finish(into: Accum): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(into.positions), 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(new Float32Array(into.colors), 3));
  geometry.computeBoundingSphere();
  return geometry;
}

/** Where one closed solid's triangles live in the variant's buffer. */
export interface PartRange {
  readonly start: number;
  readonly count: number;
}

export interface TreeVariant {
  readonly geometry: THREE.BufferGeometry;
  readonly triangleCount: number;
  /** One entry per closed solid, in emission order — the winding invariant is per part. */
  readonly partRanges: readonly PartRange[];
}

function variantFrom(into: Accum): TreeVariant {
  const triangleCount = into.positions.length / 9;
  const partRanges = into.partStarts.map((start, i) => ({
    start,
    count: (into.partStarts[i + 1] ?? triangleCount) - start,
  }));
  return { geometry: finish(into), triangleCount, partRanges };
}

/**
 * Crown lobes as fractions of the measured canopy radius, relative to the
 * measured canopy centre.
 *
 * The constraint every row satisfies, and the reason they are written as
 * fractions rather than metres: `hypot(dx, dz) + r <= 1` and `dy + up <= 1`, so
 * the crown is inscribed in the sphere node it replaces. `shade` is the step
 * along `CANOPY_RUN` the lobe sits at; its top takes one more and its underside
 * one less. Which lobe goes where is invented — see the module doc.
 */
const BROADLEAF_LOBES: readonly {
  readonly dx: number;
  readonly dy: number;
  readonly dz: number;
  readonly r: number;
  readonly up: number;
  readonly down: number;
  readonly shade: number;
}[] = [
  { dx: 0, dy: 0.47, dz: 0, r: 0.53, up: 0.53, down: 0.5, shade: 1 },
  { dx: 0.42, dy: 0.05, dz: -0.19, r: 0.53, up: 0.47, down: 0.51, shade: 0 },
  { dx: -0.31, dy: -0.09, dz: 0.35, r: 0.52, up: 0.47, down: 0.53, shade: 0 },
  { dx: -0.13, dy: -0.34, dz: -0.4, r: 0.5, up: 0.43, down: 0.5, shade: -1 },
];

/**
 * A broadleaf: a tapering trunk carrying four overlapping crown lobes, the
 * whole crown inscribed in the sphere node the original draws there.
 */
export function createBroadleaf(canopy: PaletteColour, bark: PaletteColour): TreeVariant {
  const into: Accum = { positions: [], colors: [], partStarts: [] };
  const R = CANOPY_RADIUS_M;
  trunkSpike(into, 0.4, CANOPY_CENTRE_M, bark);
  for (const l of BROADLEAF_LOBES) {
    lobe(
      into,
      [l.dx * R, CANOPY_CENTRE_M + l.dy * R, l.dz * R],
      l.r * R,
      l.up * R,
      l.down * R,
      shiftedCanopy(canopy, l.shade),
      shiftedCanopy(canopy, l.shade - 1),
    );
  }
  return variantFrom(into);
}

/**
 * Conifer tiers, as fractions of the measured radius and of the crown span, so
 * the fir tops out exactly where the sphere node does.
 */
const CONIFER_SKIRTS: readonly { readonly baseY: number; readonly apexY: number; readonly r: number; readonly shade: number }[] =
  [
    { baseY: 0.73, apexY: 1.58, r: 0.98, shade: -1 },
    { baseY: 1.41, apexY: 2.14, r: 0.72, shade: 0 },
    { baseY: 1.97, apexY: 2.64, r: 0.46, shade: 1 },
  ];

/** A conifer: the same trunk under three tapering skirts. */
export function createConifer(canopy: PaletteColour, bark: PaletteColour): TreeVariant {
  const into: Accum = { positions: [], colors: [], partStarts: [] };
  const R = CANOPY_RADIUS_M;
  trunkSpike(into, 0.3, CONIFER_SKIRTS[0]!.baseY * R + 0.8, bark);
  for (const s of CONIFER_SKIRTS) {
    skirt(
      into,
      s.baseY * R,
      s.apexY * R,
      s.r * R,
      shiftedCanopy(canopy, s.shade),
      shiftedCanopy(canopy, s.shade - 1),
    );
  }
  return variantFrom(into);
}

/**
 * The variant set: every pairing of the two canopy entries with the two bark
 * entries the shipped tree carries, in both shapes. Four variants — enough that
 * a wood is not a stamped repeat, few enough that a region's whole vegetation
 * fits in a handful of instanced batches.
 *
 * `shift` walks every canopy down its own run for the hour, the same steps the
 * ground takes (`flight/time-of-day.ts`'s `groundRampShiftFor`) — a wood that
 * stayed noon-green over a dimmed night field would be worse than no wood.
 * Bark darkens too, by dropping to the darker of the two shipped trunk entries
 * rather than off the end of a two-entry run.
 */
export function createTreeVariants(shift = 0): TreeVariant[] {
  const canopy = (base: PaletteColour): PaletteColour => shiftedCanopy(base, shift);
  const bark = (base: PaletteColour): PaletteColour => (shift < 0 ? TRUNK_COLOURS[0]! : base);
  return [
    createBroadleaf(canopy(CANOPY_COLOURS[1]!), bark(TRUNK_COLOURS[1]!)),
    createBroadleaf(canopy(CANOPY_COLOURS[0]!), bark(TRUNK_COLOURS[0]!)),
    createConifer(canopy(CANOPY_COLOURS[0]!), bark(TRUNK_COLOURS[1]!)),
    createConifer(canopy(CANOPY_COLOURS[1]!), bark(TRUNK_COLOURS[0]!)),
  ];
}
